import React, { useEffect, useRef, useState } from 'react';
import { getRowAlertStateWithColumns, ALERT_STYLES, evaluateCondition } from './utils/alertUtils';
import { formatValue, formatValueWithDecimals } from '../../utils/numberFormatter';

// URL 컬럼 감지 함수
const isUrlColumn = (columnName) => {
  const urlPatterns = ['url', 'link', 'video_url', 'website', '웹사이트', '링크', 'href', 'uri'];
  return urlPatterns.some(pattern => 
    columnName.toLowerCase().includes(pattern.toLowerCase())
  );
};

// URL 형식 검증 함수
const isValidUrl = (value) => {
  if (!value || typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

// URL 텍스트 축약 함수
const truncateUrl = (url, maxLength = 40) => {
  if (url.length <= maxLength) return url;
  return `${url.substring(0, maxLength)}...`;
};

// 테이블 알람 애니메이션 CSS를 전역으로 추가
const ALERT_CSS = `
  .alert-warning {
    animation: warning-pulse 2s ease-in-out infinite;
  }
  .alert-critical {
    animation: critical-shake 1.8s ease-in-out infinite;
  }
  .alert-emergency {
    animation: emergency-intense 1.5s ease-in-out infinite;
  }
  @keyframes warning-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.01); }
  }
  @keyframes critical-shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-2px); }
    75% { transform: translateX(2px); }
  }
  @keyframes emergency-intense {
    0%, 100% { 
      transform: scale(1); 
      box-shadow: 0 0 10px rgba(220, 53, 69, 0.3); 
    }
    50% { 
      transform: scale(1.02); 
      box-shadow: 0 0 20px rgba(220, 53, 69, 0.6); 
    }
  }
`;

// CSS 스타일을 한 번만 추가
if (typeof document !== 'undefined' && !document.getElementById('table-alert-styles')) {
  const style = document.createElement('style');
  style.id = 'table-alert-styles';
  style.innerHTML = ALERT_CSS;
  document.head.appendChild(style);
}

const TableRenderer = ({ chartData, error, onChartClick, isEditMode, componentId, onEmergencyTrigger, activeQuery, style }) => {
  // 테이블은 행별 시각적 알람 표시만 하고, Emergency 오버레이 트리거는 하지 않음
  const lastUpdatedRef = useRef(null);
  // 행 선택 상태 (단일 선택)
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  
  // 디버깅: visibility 옵션 확인
  const visibilityTitle = chartData?.options?.visibility?.title ?? chartData?.options?.style?.visibility?.title;
  
  // 테이블 스타일 옵션 추출 (기본값 포함)
  // advanced.tableOptions에서 먼저 확인, 없으면 레거시 위치(options.tableStyle) 확인
  const tableOptions = chartData?.options?.advanced?.tableOptions || chartData?.options?.tableStyle || {};
  const tableStyle = {
    showHeader: tableOptions.showHeader !== false, // 기본값 true
    showRowBorders: tableOptions.showRowBorders !== false, // 기본값 true
    showColumnBorders: tableOptions.showColumnBorders !== false, // 기본값 true
    showOuterBorder: tableOptions.showOuterBorder !== false, // 기본값 true
    stripedRows: tableOptions.stripedRows !== false, // 기본값 true
    hoverEffect: tableOptions.hoverEffect !== false, // 기본값 true
    compactMode: tableOptions.compactMode || false, // 기본값 false
    ...tableOptions
  };

  // 배경색 스타일(새 표준 위치: options.style.tableColors)
  const tableColors = chartData?.options?.style?.tableColors || {};
  const headerBgColor = tableColors.header || '#f8f9fa';
  const rowBgColor = tableColors.row || '#ffffff';
  const rowAltBgColor = tableColors.rowAlternate || 'rgba(248, 249, 250, 0.5)';
  const rowHeaderBgColor = tableColors.rowHeader; // 선택적
  // 선택 상태 스타일(표준 위치: options.style.selected)
  const selectedStyle = chartData?.options?.style?.selected || {};
  const rowBgSelected = selectedStyle.rowBgSelected || 'var(--tenant-primary-tint-12, rgba(74, 144, 226, 0.12))';
  const rowTextSelected = selectedStyle.rowTextSelected || 'var(--tenant-primary-900, #0D47A1)';
  
  // 퍼센트 기반 컬럼 너비 계산 유틸리티
  const computePercentWidths = React.useCallback((columns, spec) => {
    try {
      if (!Array.isArray(columns) || columns.length === 0 || !spec || spec.mode !== 'percent') return null;
      const keyToPercent = new Map();
      const specifiedKeys = new Set();
      const list = Array.isArray(spec.columns) ? spec.columns : [];
      list.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const k = item.key;
        const p = Number(item.percent);
        if (!k || isNaN(p) || p < 0) return;
        keyToPercent.set(k, p);
        specifiedKeys.add(k);
      });

      // 지정된 합계
      let sumSpecified = 0;
      columns.forEach((colName) => {
        if (keyToPercent.has(colName)) sumSpecified += keyToPercent.get(colName);
      });

      // 음수/NaN 방지
      if (!isFinite(sumSpecified) || sumSpecified < 0) sumSpecified = 0;

      // 지정 퍼센트 정규화 계수
      // - unspecified: 'equal' 인 경우에도 지정 합계가 100을 초과하면 100으로 스케일다운
      // - unspecified: 'auto' 인 경우 합계가 100 이하라면 스케일하지 않음(남은 폭은 자동 컬럼 차지)
      const unspecifiedMode = spec.unspecified === 'equal' ? 'equal' : 'auto';
      const shouldNormalizeDown = sumSpecified > 100;
      const normalizeFactor = shouldNormalizeDown && sumSpecified > 0 ? (100 / sumSpecified) : 1;
      const normalizedSpecified = new Map();
      columns.forEach((colName) => {
        if (keyToPercent.has(colName)) {
          const v = keyToPercent.get(colName) * normalizeFactor;
          normalizedSpecified.set(colName, v);
        }
      });

      // 사용된 퍼센트 및 남은 퍼센트 계산
      let used = 0;
      normalizedSpecified.forEach((v) => { used += v; });
      // auto 모드에서 sumSpecified < 100인 경우, 지정 퍼센트는 그대로 두고 남은 폭은 자동 컬럼이 차지
      // equal 모드에서는 남은 퍼센트를 균등 분배
      let remaining = Math.max(0, 100 - used);
      const unspecifiedCols = columns.filter((c) => !normalizedSpecified.has(c));
      const result = new Array(columns.length).fill(null);

      // equal 모드면 미지정에 남은 퍼센트를 균등 분배
      if (unspecifiedMode === 'equal' && unspecifiedCols.length > 0) {
        const each = remaining / unspecifiedCols.length;
        columns.forEach((c, i) => {
          if (normalizedSpecified.has(c)) {
            result[i] = normalizedSpecified.get(c);
          } else {
            result[i] = each;
          }
        });
        return result;
      }

      // auto 모드면 지정 컬럼만 width를 주고 나머지는 자동폭(null 유지)
      columns.forEach((c, i) => {
        if (normalizedSpecified.has(c)) {
          result[i] = normalizedSpecified.get(c);
        } else {
          result[i] = null; // 자동폭
        }
      });
      return result;
    } catch (_) {
      return null;
    }
  }, []);
  
  // 스타일 헬퍼 함수
  const getStyleFor = (target) => {
    if (!style?.fonts?.[target]) return {};
    const fontStyle = style.fonts[target];
    return {
      fontFamily: fontStyle.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: typeof fontStyle.fontSize === 'number' ? `${fontStyle.fontSize}px` : fontStyle.fontSize || '14px',
      fontWeight: fontStyle.fontWeight || 'normal',
      fontStyle: fontStyle.fontStyle || 'normal',
      color: fontStyle.color || '#333333',
      lineHeight: fontStyle.lineHeight || 1.4
    };
  };
  
  // 정렬 상태 관리
  const [sortConfig, setSortConfig] = useState({ column: null, direction: null });
  
  // lastUpdated 참조 업데이트 (애니메이션 없이 조용히 갱신)
  useEffect(() => {
    lastUpdatedRef.current = chartData?.lastUpdated;
  }, [chartData?.lastUpdated]);

  // 투명 배경 여부
  const isTransparent = chartData?.options?.transparent === true || chartData?.options?.transparent === 'true';
  
  // 칼럼 클릭 핸들러
  const handleColumnClick = (columnName) => {
    let direction = 'asc';
    if (sortConfig.column === columnName && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ column: columnName, direction });
  };
  
  // 데이터 정렬 함수
  const sortedRows = React.useMemo(() => {
    if (!chartData?.data?.rows || !sortConfig.column) {
      return chartData?.data?.rows || [];
    }
    
    const rowsCopy = [...chartData.data.rows];
    const columnIndex = chartData.data.columns.indexOf(sortConfig.column);
    
    return rowsCopy.sort((a, b) => {
      let aValue, bValue;
      
      if (Array.isArray(a)) {
        aValue = a[columnIndex];
        bValue = b[columnIndex];
      } else {
        aValue = a[sortConfig.column];
        bValue = b[sortConfig.column];
      }
      
      // 숫자 비교
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // 문자열 비교
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      aValue = String(aValue);
      bValue = String(bValue);
      
      if (sortConfig.direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  }, [chartData?.data?.rows, chartData?.data?.columns, sortConfig]);
  
  
  
  // 테이블 행별 알람 상태 처리 (시각적 표시용, Emergency 오버레이 트리거 안 함)
  
  // 알람 조건 추출
  let alertColumn, alertCondition, criticalCondition, emergencyCondition;
  if (chartData && chartData.options) {
    const options = chartData.options || {};
    const advanced = options.advanced || {};
    alertColumn = advanced.alertColumn;
    alertCondition = advanced.alertCondition;
    criticalCondition = advanced.criticalCondition;
    emergencyCondition = advanced.emergencyCondition;
  }
  
  // 테이블 제목은 부모 컴포넌트(Dashboard)에서 관리하는 것이 더 일반적일 수 있음
  // {chartData.title && <h3 style={{ textAlign: 'center', marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>{chartData.title}</h3>}
  if (error) {
    return (
      <div style={{ 
        color: '#e74c3c', 
        padding: '20px',
        textAlign: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '14px',
        backgroundColor: 'rgba(231, 76, 60, 0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(231, 76, 60, 0.1)'
      }}>
        <strong>테이블 오류:</strong> {error}
      </div>
    );
  }

  if (chartData && chartData.data && chartData.data.columns && chartData.data.rows) {
    const options = chartData.options || {};
    const advanced = options.advanced || {};
    let { alertCondition, criticalCondition, emergencyCondition, alertColumn } = advanced;
    const columnWidthSpec = chartData?.options?.style?.table?.columnWidths || null;
    const isPercentWidthMode = !!(columnWidthSpec && columnWidthSpec.mode === 'percent');
    
    // activeQuery가 있는 경우 처리
    if (activeQuery) {
      
      if (activeQuery.alertConditions) {
        // 알람 조건이 있는 배지에서 온 경우: 배지 알람 조건 적용
        alertColumn = activeQuery.alertConditions.alertColumn || alertColumn;
        alertCondition = activeQuery.alertConditions.alertCondition || alertCondition;
        criticalCondition = activeQuery.alertConditions.criticalCondition || criticalCondition;
        emergencyCondition = activeQuery.alertConditions.emergencyCondition || emergencyCondition;
        
      } else {
        // 알람 조건이 없는 배지에서 온 경우: 알람 관련 모든 설정 제거
        alertColumn = null;
        alertCondition = null;
        criticalCondition = null;
        emergencyCondition = null;
        
      }
    }
    
    // 알람 설정이 있고 알람상태 컬럼이 없으면 동적으로 추가
    const hasAlarmSettings = alertColumn && (alertCondition || criticalCondition || emergencyCondition);
    const hasAlarmColumn = chartData.data.columns.includes('알람상태') || chartData.data.columns.includes('상태');
    
    let processedChartData = chartData;
    
    // activeQuery가 있고 알람 설정이 없는 경우: 기존 알람 컬럼 제거
    if (activeQuery && !hasAlarmSettings && hasAlarmColumn) {
      const filteredColumns = chartData.data.columns.filter(col => col !== '알람상태' && col !== '상태');
      const filteredRows = chartData.data.rows.map(row => {
        if (Array.isArray(row)) {
          return row.filter((_, index) => {
            const colName = chartData.data.columns[index];
            return colName !== '알람상태' && colName !== '상태';
          });
        } else {
          const { 알람상태, 상태, ...filteredRow } = row;
          return filteredRow;
        }
      });
      
      processedChartData = {
        ...chartData,
        data: {
          ...chartData.data,
          columns: filteredColumns,
          rows: filteredRows
        }
      };
      
    }
    // [GS인증 수정] 알람상태 컬럼 자동 추가 비활성화: 쿼리 결과에 없는 컬럼을 강제로 추가하지 않음
    // } else if (hasAlarmSettings && !hasAlarmColumn) {
    //   // 알람상태 컬럼 동적 추가
    //   const newColumns = [...chartData.data.columns, '알람상태'];
    //   const newRows = chartData.data.rows.map(row => {
    //     // 알람 기준 컬럼의 값으로 알람상태 계산
    //     const targetColumnIndex = chartData.data.columns.indexOf(alertColumn);
    //     let alarmValue = '✅정상';
    //
    //     if (targetColumnIndex >= 0) {
    //       const cellValue = Array.isArray(row) ? row[targetColumnIndex] : row[alertColumn];
    //       const numValue = Number(cellValue);
    //
    //       if (emergencyCondition && evaluateCondition(emergencyCondition, numValue)) {
    //         alarmValue = '🚨긴급';
    //       } else if (criticalCondition && evaluateCondition(criticalCondition, numValue)) {
    //         alarmValue = '⚠️경고';
    //       } else if (alertCondition && evaluateCondition(alertCondition, numValue)) {
    //         alarmValue = '📢주의';
    //       }
    //     }
    //
    //     return Array.isArray(row) ? [...row, alarmValue] : { ...row, '알람상태': alarmValue };
    //   });
    //
    //   processedChartData = {
    //     ...chartData,
    //     data: {
    //       ...chartData.data,
    //       columns: newColumns,
    //       rows: newRows
    //     }
    //   };
    // }
    
    // 디버깅: 알람 조건 확인
    
    return (
      <>
        {/* CSS 애니메이션 정의 */}
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes fadeInOut {
            0% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
          
          @keyframes table-warning-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.8; }
          }
          
          @keyframes table-critical-shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-2px); }
            75% { transform: translateX(2px); }
          }
          
          @keyframes table-emergency-intense {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.02); }
          }
          
          @keyframes table-icon-rotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          .table-alert-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            font-size: 12px;
            font-weight: bold;
            margin-right: 8px;
            flex-shrink: 0;
            vertical-align: middle;
          }
          
          .table-alert-icon.emergency {
            animation: table-icon-rotate 2s linear infinite;
          }
          
          tr.alert-warning {
            animation: table-warning-pulse 2s ease-in-out infinite;
          }
          
          tr.alert-critical {
            animation: table-critical-shake 1.8s ease-in-out infinite;
          }
          
          tr.alert-emergency {
            animation: table-emergency-intense 1.5s ease-in-out infinite;
          }
        `}} />
        
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: 'none', // 외부 테두리 제거
          borderRadius: '0', // 외부 라운드 제거
          backgroundColor: 'transparent', // 배경 투명
          margin: 0,
          padding: 0
        }}>
        {/* 테이블 제목 표시 */}
        {chartData.options?.showTitle !== false && (chartData.title || chartData.options?.title) && (
          <div style={{
            textAlign: 'center',
            padding: '16px 16px 12px 16px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            backgroundColor: 'rgba(248, 249, 250, 0.8)',
            borderTopLeftRadius: '0',
            borderTopRightRadius: '0',
            ...getStyleFor('title')
          }}>
            {chartData.title || chartData.options?.title}
          </div>
        )}
        
        {/* 스크롤 가능한 테이블 컨테이너 - 데이터 갱신 시 깜빡임 없이 조용히 갱신 */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          // flex 컨테이너 내에서 남는 공간을 모두 차지하도록 설정
          // 고정 maxHeight로 인해 큰 컴포넌트 높이에서 하단에 빈 영역이 생기던 문제 수정
          // 참고: flex 아이템이 스크롤 영역이 되려면 minHeight: 0 이 필요함
          minHeight: 0,
          height: '100%',
          // 스크롤바 스타일링
          scrollbarWidth: 'thin',
          scrollbarColor: '#c1c1c1 #f1f1f1',
          // 외곽 테두리 조건부 적용
          border: tableStyle.showOuterBorder ? '1px solid #e9ecef' : 'none',
          borderRadius: tableStyle.showOuterBorder ? '4px' : '0'
        }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: tableStyle.showRowBorders || tableStyle.showColumnBorders ? 'collapse' : 'separate',
            borderSpacing: !tableStyle.showRowBorders && !tableStyle.showColumnBorders ? '0' : '0',
            fontSize: '13px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            minWidth: isPercentWidthMode ? undefined : 'max-content',
            tableLayout: isPercentWidthMode ? 'fixed' : undefined
          }}>
            {/* 퍼센트 모드일 때 colgroup으로 폭 지정 */}
            {(() => {
              try {
                if (!columnWidthSpec || columnWidthSpec.mode !== 'percent') return null;
                const colPercents = computePercentWidths(processedChartData.data.columns, columnWidthSpec);
                if (!colPercents) return null;
                return (
                  <colgroup>
                    {processedChartData.data.columns.map((_, idx) => (
                      <col key={idx} style={colPercents[idx] != null ? { width: `${colPercents[idx]}%` } : {}} />
                    ))}
                  </colgroup>
                );
              } catch (_) { return null; }
            })()}
            {tableStyle.showHeader && (
              <thead style={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: headerBgColor,
                boxShadow: tableStyle.showRowBorders ? '0 2px 4px rgba(0,0,0,0.08)' : 'none'
              }}>
                <tr style={{ borderBottom: tableStyle.showRowBorders ? '2px solid #e9ecef' : 'none' }}>
                  {processedChartData.data.columns.map((col, index) => (
                    <th 
                      key={index} 
                      onClick={!isEditMode ? () => handleColumnClick(col) : undefined}
                      style={{ 
                        padding: tableStyle.compactMode ? '8px 12px' : '14px 16px',
                        textAlign: 'left',
                        backgroundColor: headerBgColor,
                        borderRight: tableStyle.showColumnBorders && index < processedChartData.data.columns.length - 1 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                        whiteSpace: 'nowrap',
                        minWidth: isPercentWidthMode ? 0 : '100px',
                        cursor: !isEditMode ? 'pointer' : 'default',
                        userSelect: 'none',
                        position: 'relative',
                        transition: 'background-color 0.2s ease',
                        ...getStyleFor('tableHeader')
                      }}
                      onMouseEnter={!isEditMode ? (e) => {
                        if (tableStyle.hoverEffect) {
                          e.currentTarget.style.backgroundColor = '#e9ecef';
                        }
                      } : undefined}
                      onMouseLeave={!isEditMode ? (e) => {
                        e.currentTarget.style.backgroundColor = headerBgColor;
                      } : undefined}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {col}
                        {!isEditMode && sortConfig.column === col && (
                          <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                            {sortConfig.direction === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {(() => {
                // processedChartData 사용
                const dataToSort = processedChartData.data.rows || [];
                
                // 정렬 로직을 processedChartData에 적용
                const sortedRows = (() => {
                  if (!dataToSort || !sortConfig.column) {
                    return dataToSort;
                  }
                  
                  const rowsCopy = [...dataToSort];
                  const columnIndex = processedChartData.data.columns.indexOf(sortConfig.column);
                  
                  return rowsCopy.sort((a, b) => {
                    let aValue, bValue;
                    
                    if (Array.isArray(a)) {
                      aValue = a[columnIndex];
                      bValue = b[columnIndex];
                    } else {
                      aValue = a[sortConfig.column];
                      bValue = b[sortConfig.column];
                    }
                    
                    // 숫자 비교
                    const aNum = parseFloat(aValue);
                    const bNum = parseFloat(bValue);
                    
                    if (!isNaN(aNum) && !isNaN(bNum)) {
                      return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
                    }
                    
                    // 문자열 비교
                    if (aValue === null || aValue === undefined) aValue = '';
                    if (bValue === null || bValue === undefined) bValue = '';
                    
                    return sortConfig.direction === 'asc' ? 
                      String(aValue).localeCompare(String(bValue)) : 
                      String(bValue).localeCompare(String(aValue));
                  });
                })();
                
                return sortedRows.map((row, rowIndex) => {
                // 알림 상태 확인 (processedChartData 사용)
                const alertState = alertColumn ? getRowAlertStateWithColumns(row, processedChartData.data.columns, alertColumn, { alertCondition, criticalCondition, emergencyCondition }) : 'default';
                const alertStyle = ALERT_STYLES[alertState];
                // 줄무늬 효과 적용 여부에 따른 배경색
                const defaultBgColor = tableStyle.stripedRows 
                  ? (rowIndex % 2 === 0 ? rowBgColor : rowAltBgColor)
                  : rowBgColor;
                const isSelectedRow = selectedRowIndex === rowIndex;
                
                // 디버깅: 테이블 알람 상태 확인 (첫 5개 행만)
                if (rowIndex < 5 && alertColumn) {
                  let value;
                  if (Array.isArray(row)) {
                    const columnIndex = processedChartData.data.columns.indexOf(alertColumn);
                    value = row[columnIndex];
                  } else {
                    value = row[alertColumn];
                  }
                }
                

                
                return (
                  <tr 
                    key={rowIndex}
                    className={`${alertState !== 'default' ? `alert-${alertState}` : ''} ${isSelectedRow ? 'row-selected' : ''}`}
                    style={{ 
                      borderBottom: tableStyle.showRowBorders ? '1px solid rgba(0, 0, 0, 0.05)' : 'none',
                      backgroundColor: isSelectedRow
                        ? rowBgSelected
                        : (alertState !== 'default' ? alertStyle.backgroundColor : defaultBgColor),
                      transition: 'background-color 0.2s ease',
                      color: isSelectedRow ? rowTextSelected : (alertState !== 'default' ? alertStyle.color : '#495057'),
                      cursor: (!isEditMode && onChartClick) ? 'pointer' : 'default',
                    }}
                    aria-selected={isSelectedRow ? 'true' : 'false'}
                    tabIndex={isEditMode ? -1 : 0}
                    onClick={!isEditMode && onChartClick ? () => {
                      setSelectedRowIndex(rowIndex);
                      // 행 데이터를 col1, col2, col3... 형태로 변환
                      const clickData = {};
                      processedChartData.data.columns.forEach((col, idx) => {
                        const value = Array.isArray(row) ? row[idx] : row[col];
                        clickData[`col${idx + 1}`] = value;
                      });
                      // 추가 메타 포함: row, rowIndex, colNames, value
                      clickData.row = Array.isArray(row)
                        ? processedChartData.data.columns.reduce((acc, colName, idx) => {
                            acc[colName] = row[idx];
                            return acc;
                          }, {})
                        : row;
                      clickData.rowIndex = rowIndex;
                      clickData.colNames = processedChartData.data.columns;
                      clickData.value = clickData.row;
                      // handleChartClick은 (clickedData, eventType) 형식으로 받음
                      onChartClick(clickData, 'table');
                    } : undefined}
                    onKeyDown={!isEditMode && onChartClick ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedRowIndex(rowIndex);
                        const clickData = {};
                        processedChartData.data.columns.forEach((col, idx) => {
                          const value = Array.isArray(row) ? row[idx] : row[col];
                          clickData[`col${idx + 1}`] = value;
                        });
                        clickData.row = Array.isArray(row)
                          ? processedChartData.data.columns.reduce((acc, colName, idx) => {
                              acc[colName] = row[idx];
                              return acc;
                            }, {})
                          : row;
                        clickData.rowIndex = rowIndex;
                        clickData.colNames = processedChartData.data.columns;
                        clickData.value = clickData.row;
                        onChartClick(clickData, 'table');
                      }
                    } : undefined}
                    onMouseEnter={!isEditMode ? (e) => {
                      if (tableStyle.hoverEffect && alertState === 'default' && !isSelectedRow) {
                        e.target.closest('tr').style.backgroundColor = 'rgba(74, 144, 226, 0.08)';
                      }
                      if (onChartClick) {
                        e.target.closest('tr').style.cursor = 'pointer';
                      }
                    } : undefined}
                    onMouseLeave={!isEditMode ? (e) => {
                      if (!isSelectedRow) {
                        if (alertState === 'default') {
                          e.target.closest('tr').style.backgroundColor = defaultBgColor;
                        } else {
                          e.target.closest('tr').style.backgroundColor = alertStyle.backgroundColor;
                        }
                      }
                    } : undefined}
                  >
                    {processedChartData.data.columns.map((col, colIndex) => (
                      <td 
                        key={colIndex} 
                        style={{ 
                          padding: tableStyle.compactMode ? '8px 12px' : '12px 16px',
                          borderRight: tableStyle.showColumnBorders && colIndex < processedChartData.data.columns.length - 1 ? '1px solid rgba(0, 0, 0, 0.04)' : 'none',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: isPercentWidthMode ? 'none' : '200px',
                          minWidth: isPercentWidthMode ? 0 : undefined,
                          position: 'relative',
                          // 첫 번째 열에 행 헤더 배경색이 지정된 경우 오버라이드
                          ...(colIndex === 0 && rowHeaderBgColor ? { backgroundColor: rowHeaderBgColor } : {}),
                          ...getStyleFor('tableCell'),
                          color: alertState !== 'default' ? alertStyle.color : getStyleFor('tableCell').color || '#495057'
                        }}
                        title={Array.isArray(row) ? row[colIndex] : row[col]}
                      >
                        {/* 첫 번째 컬럼에 알림 아이콘 표시 */}
                        {colIndex === 0 && alertState !== 'default' && alertStyle.icon && (
                          <span 
                            className={`table-alert-icon ${alertState === 'emergency' ? 'emergency' : ''}`}
                            style={{
                              backgroundColor: alertStyle.iconBackground,
                              color: alertStyle.iconColor,
                              marginRight: '8px'
                            }}
                          >
                            {alertStyle.icon}
                          </span>
                        )}
                        <span>{(() => {
                          const cellValue = Array.isArray(row) ? row[colIndex] : row[col];

                          // 컬럼별 포맷팅 함수
                          const formatCellValue = (value, columnName, columnIndex) => {
                            // URL이나 특수 컬럼은 그대로 반환
                            if (isUrlColumn(columnName)) return value;

                            // 날짜/시간 패턴 감지 (YYYY-MM-DD, HH:MM, datetime 등)
                            const isDateTimeValue = (val) => {
                              if (typeof val !== 'string') return false;
                              // 날짜 패턴들: 2025-01-17, 2025/01/17, 14:30, 2025-01-17 14:30 등
                              const dateTimePatterns = [
                                /^\d{4}[-\/]\d{1,2}[-\/]\d{1,2}/, // YYYY-MM-DD or YYYY/MM/DD
                                /^\d{1,2}[-\/]\d{1,2}[-\/]\d{4}/, // DD-MM-YYYY or MM/DD/YYYY
                                /\d{1,2}:\d{2}/, // HH:MM 포함
                                /[년월일시분초]/, // 한글 날짜/시간
                                /AM|PM|am|pm/, // 12시간 형식
                                /Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec/i // 영문 월
                              ];
                              return dateTimePatterns.some(pattern => pattern.test(val));
                            };

                            // 날짜/시간 값은 포맷팅하지 않고 그대로 반환
                            if (isDateTimeValue(value)) {
                              return value;
                            }

                            // columnFormats 확인 (options.advanced.columnFormats)
                            const formats = chartData?.options?.advanced?.columnFormats;
                            if (formats) {
                              // 컬럼명으로 먼저 확인
                              const format = formats[columnName] || formats[`col_${columnIndex + 1}`];
                              if (format && format.decimalPlaces !== undefined) {
                                return formatValueWithDecimals(value, format.decimalPlaces);
                              }
                            }

                            // 기본 소수점 자리수 확인 (defaultDecimalPlaces)
                            const defaultDecimalPlaces = chartData?.options?.advanced?.defaultDecimalPlaces;

                            // 순수 숫자인 경우만 포맷팅 적용
                            // 문자열인 경우, 전체가 숫자로 변환 가능한지 확인
                            if (value !== null && value !== undefined && value !== '') {
                              const strValue = String(value).trim();
                              const numValue = parseFloat(strValue);
                              // 파싱된 숫자를 다시 문자열로 변환했을 때 원본과 같거나,
                              // 소수점이 포함된 경우 확인
                              if (!isNaN(numValue) && (
                                strValue === String(numValue) ||
                                strValue === numValue.toFixed(1) ||
                                strValue === numValue.toFixed(2) ||
                                /^-?\d+\.?\d*$/.test(strValue) // 순수 숫자 형식
                              )) {
                                return formatValueWithDecimals(value, defaultDecimalPlaces ?? null);
                              }
                            }

                            return value;
                          };

                          // 알람상태 또는 상태 컬럼인 경우 동적으로 생성
                          if (col === '알람상태' || col === '상태') {
                            // 알람 기준이 되는 컬럼 찾기 (alertColumn 사용, 없으면 대기인원)
                            const targetColumn = alertColumn || '대기인원';
                            let peopleColumnIndex = chartData.data.columns.indexOf(targetColumn);
                            
                            // 대기인원이 없으면 최대대기인원 등 다른 숫자 컬럼 찾기
                            if (peopleColumnIndex === -1) {
                              peopleColumnIndex = chartData.data.columns.indexOf('최대대기인원');
                            }
                            
                            const peopleCount = Array.isArray(row) ? row[peopleColumnIndex] : row[targetColumn];
                            
                            if (peopleCount !== null && peopleCount !== undefined) {
                              const numValue = Number(peopleCount);
                              if (emergencyCondition && evaluateCondition(emergencyCondition, numValue)) {
                                return '🚨긴급';
                              } else if (criticalCondition && evaluateCondition(criticalCondition, numValue)) {
                                return '⚠️경고';
                              } else if (alertCondition && evaluateCondition(alertCondition, numValue)) {
                                return '📢주의';
                              } else {
                                return '✅정상';
                              }
                            }
                            return '✅정상';
                          }
                          
                          // URL 컬럼인 경우 클릭 가능한 링크로 변환
                          if (isUrlColumn(col) && isValidUrl(cellValue)) {
                            return (
                              <a 
                                href={cellValue} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{
                                  color: '#1890ff',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontSize: '13px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.color = '#40a9ff';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.color = '#1890ff';
                                }}
                                onClick={(e) => {
                                  e.stopPropagation(); // 테이블 행 클릭 이벤트 방지
                                }}
                                title={cellValue} // 전체 URL을 툴팁으로 표시
                              >
                                {truncateUrl(cellValue)}
                              </a>
                            );
                          }
                          
                          // 컬럼별 포맷팅 적용
                          return formatCellValue(cellValue, col, colIndex);
                        })()}                        </span>
                      </td>
                    ))}
                  </tr>
                );
                });
              })()}
              {/* 데이터가 없을 경우 메시지 표시 */}
              {processedChartData.data.rows.length === 0 && (
                 <tr>
                    <td 
                      colSpan={processedChartData.data.columns.length} 
                      style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px', 
                        color: '#6c757d',
                        fontStyle: 'italic',
                        backgroundColor: 'rgba(248, 249, 250, 0.5)',
                        fontSize: '14px'
                      }}
                    >
                       데이터가 없습니다.
                    </td>
                 </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* 테이블 정보 표시 */}
        {sortedRows.length > 0 && (
          <div style={{
            padding: '8px 16px', // 패딩 줄임
            backgroundColor: 'rgba(248, 249, 250, 0.8)', // 투명도 증가
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
            fontSize: '12px',
            color: '#6c757d',
            textAlign: 'right',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            borderBottomLeftRadius: '0', // 라운드 제거
            borderBottomRightRadius: '0' // 라운드 제거
          }}>
            총 {sortedRows.length}개 행 | {chartData.data.columns.length}개 컬럼
          </div>
        )}
      </div>
      </>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      textAlign: 'center', 
      color: '#6c757d',
      border: 'none',
      borderRadius: '0',
      backgroundColor: 'transparent',
      fontSize: '14px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      테이블 데이터를 로드 중이거나 형식이 올바르지 않습니다. (columns, rows 필요)
    </div>
  );
};

export default React.memo(TableRenderer); 